import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';
import { bookingsAPI, waitlistAPI } from '../../../services/api';
import { ArrowLeft, Edit, Trash2, CheckCircle, Home, Calendar, Clock, User, Phone, Mail, Navigation, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';

const BookingDetail = () => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();
    const { id } = useParams();

    const [booking, setBooking] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);

    // Convert states
    const [showConvertModal, setShowConvertModal] = useState(false);
    const [convertData, setConvertData] = useState({
        rent: '',
        contractStartDate: '',
        contractEndDate: '',
        paymentDate: ''
    });

    useEffect(() => {
        fetchBooking();
    }, [id]);

    const fetchBooking = async () => {
        try {
            setLoading(true);
            const res = await bookingsAPI.getById(id);
            setBooking(res.booking);
        } catch (error) {
            console.error('Error fetching booking details:', error);
            toast.error(t('error_loading_data'));
            navigate('/admin/bookings');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async () => {
        if (!window.confirm(t('confirm_booking_question'))) return;
        try {
            setActionLoading(true);
            await bookingsAPI.confirm(id);
            toast.success(t('booking_confirmed'));
            fetchBooking();
        } catch (error) {
            console.error('Confirm error:', error);
            toast.error(t('failed_confirm_booking'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleCancel = async () => {
        const reason = prompt(t('reason_optional'));
        if (reason === null) return; // User cancelled prompt

        try {
            setActionLoading(true);
            await bookingsAPI.cancel(id, reason);
            toast.success(t('booking_cancelled'));
            fetchBooking();
        } catch (error) {
            console.error('Cancel error:', error);
            toast.error(t('failed_cancel_booking'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleConvert = async (e) => {
        e.preventDefault();
        try {
            setActionLoading(true);
            const res = await bookingsAPI.convertToTenant(id, convertData);
            toast.success(t('booking_converted'));
            setShowConvertModal(false);
            fetchBooking();
            // Optional: navigate(`/tenants/${res.tenant.id}`)
        } catch (error) {
            console.error('Convert error:', error);
            toast.error(error.response?.data?.message || t('failed_convert_tenant'));
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div></div>;
    }

    if (!booking) return null;

    const getStatusStyle = (status) => {
        switch (status) {
            case 'pending': return isDarkMode ? 'bg-yellow-900/40 text-yellow-500 border-yellow-800' : 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'confirmed': return isDarkMode ? 'bg-blue-900/40 text-blue-400 border-blue-800' : 'bg-blue-100 text-blue-800 border-blue-200';
            case 'converted': return isDarkMode ? 'bg-green-900/40 text-green-400 border-green-800' : 'bg-green-100 text-green-800 border-green-200';
            case 'expired':
            case 'cancelled': return isDarkMode ? 'bg-red-900/40 text-red-400 border-red-800' : 'bg-red-100 text-red-800 border-red-200';
            default: return isDarkMode ? 'bg-zinc-800 text-zinc-400 border-zinc-700' : 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    return (
        <div className="max-w-5xl mx-auto space-y-6 relative">
            {/* Header / Actions */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex items-center gap-4">
                    <Link to="/admin/bookings" className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-zinc-400 hover:bg-zinc-800 hover:text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'}`}>
                        <ArrowLeft size={20} />
                    </Link>
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {t('booking_number', { id: booking.id })}
                            </h1>
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusStyle(booking.status)} capitalize`}>
                                {t(booking.status)}
                            </span>
                        </div>
                        <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                            {t('created_at', { date: booking.createdAt ? new Date(booking.createdAt).toLocaleDateString() : t('none') })} {t('created_by', { name: booking.createdByName || t('system') })}
                        </p>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2">
                    {booking.status === 'pending' && (
                        <>
                            <Link to={`/admin/bookings/${booking.id}/edit`} className={`px-4 py-2 rounded-lg border text-sm font-medium flex items-center transition-colors ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                                <Edit size={16} className="mr-2" /> {t('edit')}
                            </Link>
                            <button
                                onClick={handleConfirm} disabled={actionLoading}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center transition-colors"
                            >
                                <CheckCircle size={16} className="mr-2" /> {t('confirm')}
                            </button>
                        </>
                    )}
                    {booking.status === 'confirmed' && (
                        <button
                            onClick={() => setShowConvertModal(true)} disabled={actionLoading}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center transition-colors"
                        >
                            <Home size={16} className="mr-2" /> {t('convert_to_tenant')}
                        </button>
                    )}
                    {['pending', 'confirmed'].includes(booking.status) && (
                        <button
                            onClick={handleCancel} disabled={actionLoading}
                            className={`px-4 py-2 rounded-lg border text-sm font-medium flex items-center transition-colors ${isDarkMode ? 'border-red-900/50 bg-red-900/20 text-red-500 hover:bg-red-900/40' : 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100'}`}
                        >
                            <Trash2 size={16} className="mr-2" /> {t('cancel')}
                        </button>
                    )}
                </div>
            </div>

            {/* Grid layout */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                {/* Left Column - Details */}
                <div className="md:col-span-2 space-y-6">

                    {/* Apartment & Dates */}
                    <div className={`p-6 rounded-xl border flex flex-col md:flex-row gap-6 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}>
                        <div className="flex-1">
                            <h3 className={`text-xs font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? 'text-zinc-500' : 'text-gray-500'}`}>{t('target_property')}</h3>
                            <div className="flex items-start gap-4">
                                <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-gray-100 text-gray-700'}`}>
                                    <Building2 size={24} />
                                </div>
                                <div>
                                    <h4 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('apartment_display', { number: booking.apartmentNumber })}</h4>
                                    <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>{booking.buildingName} • {t('floor_display', { number: booking.floor })}</p>
                                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-gray-500'}`}>
                                        {booking.apartmentType || t('apartment')} • {booking.isFurnished ? t('furnished') : t('unfurnished')}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className={`hidden md:block w-px ${isDarkMode ? 'bg-zinc-800' : 'bg-gray-200'}`}></div>

                        <div className="flex-1">
                            <h3 className={`text-xs font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? 'text-zinc-500' : 'text-gray-500'}`}>{t('booking_window')}</h3>
                            <div className="space-y-3">
                                <div className="flex items-center gap-3">
                                    <Navigation size={16} className={isDarkMode ? 'text-zinc-500' : 'text-gray-400'} />
                                    <div>
                                        <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-gray-500'}`}>{t('move_in')}</p>
                                        <p className={`font-medium ${isDarkMode ? 'text-zinc-300' : 'text-gray-900'}`}>{booking.moveInDate ? new Date(booking.moveInDate).toLocaleDateString() : t('none')}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Calendar size={16} className={isDarkMode ? 'text-zinc-500' : 'text-gray-400'} />
                                    <div>
                                        <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-gray-500'}`}>{t('move_out')}</p>
                                        <p className={`font-medium ${isDarkMode ? 'text-zinc-300' : 'text-gray-900'}`}>{booking.moveOutDate ? `${new Date(booking.moveOutDate).toLocaleDateString()} ${t('at_12_pm')}` : t('indefinite')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Prospect Details */}
                    <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}>
                        <h3 className={`text-xs font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? 'text-zinc-500' : 'text-gray-500'}`}>{t('prospect_details')}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="flex items-center gap-3">
                                <User size={18} className={isDarkMode ? 'text-zinc-500' : 'text-gray-400'} />
                                <div>
                                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-gray-500'}`}>{t('prospect_name')}</p>
                                    <p className={`font-medium ${isDarkMode ? 'text-zinc-300' : 'text-gray-900'}`}>{booking.prospectName}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Phone size={18} className={isDarkMode ? 'text-zinc-500' : 'text-gray-400'} />
                                <div>
                                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-gray-500'}`}>{t('prospect_phone')}</p>
                                    <a href={`tel:${booking.prospectPhone}`} className={`font-medium transition-colors ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'}`}>{booking.prospectPhone}</a>
                                </div>
                            </div>
                            {booking.prospectEmail && (
                                <div className="flex items-center gap-3 md:col-span-2">
                                    <Mail size={18} className={isDarkMode ? 'text-zinc-500' : 'text-gray-400'} />
                                    <div>
                                        <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-gray-500'}`}>{t('prospect_email')}</p>
                                        <a href={`mailto:${booking.prospectEmail}`} className={`font-medium transition-colors ${isDarkMode ? 'text-zinc-300 hover:text-white' : 'text-gray-900 hover:text-red-600'}`}>{booking.prospectEmail}</a>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {booking.notes && (
                        <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}>
                            <h3 className={`text-xs font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? 'text-zinc-500' : 'text-gray-500'}`}>{t('internal_notes')}</h3>
                            <p className={`text-sm whitespace-pre-wrap ${isDarkMode ? 'text-zinc-300' : 'text-gray-700'}`}>{booking.notes}</p>
                        </div>
                    )}
                </div>

                {/* Right Column - Timeline */}
                <div className="space-y-6">
                    <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}>
                        <h3 className={`text-xs font-semibold uppercase tracking-wider mb-6 ${isDarkMode ? 'text-zinc-500' : 'text-gray-500'}`}>{t('booking_timeline')}</h3>

                        <div className="relative border-l border-red-500 ml-3 space-y-6 pb-2">
                            {/* Created */}
                            <div className="relative pl-6">
                                <div className="absolute w-3 h-3 bg-red-500 rounded-full -left-1.5 top-1 border border-white dark:border-zinc-900"></div>
                                <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('created_source', { source: booking.source })}</p>
                                <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-gray-500'}`}>{booking.createdAt ? new Date(booking.createdAt).toLocaleString() : t('none')}</p>
                            </div>

                            {/* Pending/Hold */}
                            {booking.status === 'pending' && booking.expiresAt && (
                                <div className="relative pl-6">
                                    <div className="absolute w-3 h-3 bg-yellow-500 rounded-full -left-1.5 top-1 border border-white dark:border-zinc-900"></div>
                                    <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('hold_expires')}</p>
                                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-gray-500'}`}>{booking.expiresAt ? new Date(booking.expiresAt).toLocaleString() : t('none')}</p>
                                </div>
                            )}

                            {/* Converted */}
                            {booking.status === 'converted' && (
                                <div className="relative pl-6">
                                    <div className="absolute w-3 h-3 bg-green-500 rounded-full -left-1.5 top-1 border border-white dark:border-zinc-900"></div>
                                    <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('converted_to_tenant')}</p>
                                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-gray-500'}`}>{booking.convertedAt ? new Date(booking.convertedAt).toLocaleString() : t('none')}</p>
                                    <Link to={`/tenants/${booking.convertedTenantId}`} className={`text-xs mt-1 block font-medium ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'}`}>
                                        {t('view_tenant_profile')} →
                                    </Link>
                                </div>
                            )}

                            {/* Cancelled */}
                            {booking.status === 'cancelled' && (
                                <div className="relative pl-6">
                                    <div className="absolute w-3 h-3 bg-red-500 rounded-full -left-1.5 top-1 border border-white dark:border-zinc-900"></div>
                                    <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('cancelled')}</p>
                                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-gray-500'}`}>{booking.cancelledAt ? new Date(booking.cancelledAt).toLocaleString() : t('none')}</p>
                                    {booking.cancellationReason && (
                                        <p className={`text-xs mt-1 italic ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>"{booking.cancellationReason}"</p>
                                    )}
                                </div>
                            )}

                            {/* Expired */}
                            {booking.status === 'expired' && (
                                <div className="relative pl-6">
                                    <div className="absolute w-3 h-3 bg-gray-500 rounded-full -left-1.5 top-1 border border-white dark:border-zinc-900"></div>
                                    <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('hold_expired')}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Convert Modal */}
            {showConvertModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className={`w-full max-w-lg rounded-xl shadow-xl overflow-hidden ${isDarkMode ? 'bg-zinc-900' : 'bg-white'}`}>
                        <div className={`px-6 py-4 border-b flex justify-between items-center ${isDarkMode ? 'border-zinc-800' : 'border-gray-200'}`}>
                            <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('convert_to_active_tenant')}</h3>
                            <button onClick={() => setShowConvertModal(false)} className={isDarkMode ? 'text-zinc-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}>✕</button>
                        </div>

                        <form onSubmit={handleConvert} className="p-6 space-y-4">
                            <p className={`text-sm mb-4 ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                                {t('lease_details_move_in_instruction', { name: booking.prospectName, unit: booking.apartmentNumber })}
                            </p>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-zinc-300' : 'text-gray-700'}`}>{t('monthly_rent')}</label>
                                    <input
                                        type="number" required min="0"
                                        value={convertData.rent} onChange={(e) => setConvertData({ ...convertData, rent: e.target.value })}
                                        className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300'}`}
                                        placeholder={t('amount')}
                                    />
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-zinc-300' : 'text-gray-700'}`}>{t('first_payment_date')}</label>
                                    <input
                                        type="date" required
                                        value={convertData.paymentDate} onChange={(e) => setConvertData({ ...convertData, paymentDate: e.target.value })}
                                        className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300'}`}
                                    />
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-zinc-300' : 'text-gray-700'}`}>{t('contract_start')}</label>
                                    <input
                                        type="date" required
                                        value={convertData.contractStartDate} onChange={(e) => setConvertData({ ...convertData, contractStartDate: e.target.value })}
                                        className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300'}`}
                                    />
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-zinc-300' : 'text-gray-700'}`}>{t('contract_end')}</label>
                                    <input
                                        type="date" required
                                        value={convertData.contractEndDate} onChange={(e) => setConvertData({ ...convertData, contractEndDate: e.target.value })}
                                        className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300'}`}
                                    />
                                </div>
                            </div>

                            <div className={`mt-6 pt-4 border-t flex justify-end gap-3 ${isDarkMode ? 'border-zinc-800' : 'border-gray-200'}`}>
                                <button type="button" onClick={() => setShowConvertModal(false)} className={`px-4 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{t('cancel')}</button>
                                <button type="submit" disabled={actionLoading} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors">{t('confirm_convert')}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookingDetail;
